import { FondCadreProvider } from '@taki/ui/cadre-canvas';
import {
  createContext,
  lazy, // [MENU DEV]
  Suspense, // [MENU DEV]
  useEffect,
  useContext,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ComponentType,
  type JSX,
} from 'react';
import { ClocheNotifications } from './notifications/ClocheNotifications.js';
import { createRoot, type Root } from 'react-dom/client';
import type { TakiConfig } from '@taki/runtime-config';
import {
  BROUILLON_ID,
  cn,
  NavigationProvider,
  PendingActionProvider,
  RAISONS_ACCES,
  ScreenNavProvider,
  ScreenUrlProvider,
  ThemeProvider,
  useAccesPort,
  useBrandingPort,
  useDeclarationsPort,
  useInstallationPort,
  useOverlayMotion,
  LIA_SCREEN_EVENT,
  type LiaScreenState,
} from '@taki/ui';
import type {
  NavigationPort,
  PendingAppAction,
  ScreenNav,
  ScreenSearch,
  ScreenUrlPort,
} from '@taki/ui';
import {
  createBrowserHistory,
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  RouterProvider,
  useNavigate,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { AuthGuard } from './AuthGuard.js';
import { BootScreen } from './surfaces/BootScreen.js';
import { readStudioOperationMarker } from './studioOperationMarker.js';
import { useDisplayScale } from './uiScale.js';
import { buildAppEntries } from './appRegistry.js';
import { useEntreesEffectives } from './ficheEffective.js';
import { destinationPour, porteOuvrable } from './navigation.js';
import {
  completerOrigine,
  decorerHistorique,
  entreeDeLEtat,
  lireOrigine,
  majEtat,
  rangDeLEtat,
} from './historique.js';
import { catalogueDeLaFiche, declarationDeLApp } from './centre/suiviEnMots.js';
import DeprecatedBand from './DeprecatedBand.js';
import { Dock } from './Dock.js';
import { useDockAutoPin } from './dockAutoPin.js';
import { useEntreesInstallees } from './installation.js';
import { HomePanel } from './HomePanel.js';
import type { HomeSection } from './HomePanel.js';
import { SettingsHubScreen } from './screens/SettingsHubScreen.js';
import { cleDOnglet } from './screens/ongletsParametres.js';
import { JournalScreen } from './screens/JournalScreen.js';
import { Notch } from './surfaces/Notch.js';
import { BrouillonsDuCompte } from './centre/BrouillonsDuCompte.js';
import { CentreCommunication } from './centre/CentreCommunication.js';
import { ouvrirCentre } from './centre/ouvrirCentre.js';
import { TakiProgressBar } from './surfaces/TakiProgressBar.js';
import type { ShellAppEntry, ShellAppRegistry } from './types.js';
import { OuverturesIaContext } from './studio/MentionHorsIa.js';
import { famillesOuvertesDesFiches } from './studio/ouvertures-ia.js';
// [STUDIO] LE CONTRAT DE PROPS DE `/studio/$appId`, partage par les deux
// composants qui peuvent la servir. Import de TYPE : il s'efface au build, il
// ne tire donc aucun des deux ecrans dans le chunk du shell.
import type { StudioRouteProps } from './studio/instance/StudioInstanceScreen.js';
// [MENU DEV] Bloc temporaire, suppression : packages/shell/src/dev/README.md
// Le point d'entree UNIQUE de "quelles apps s'affichent" : deprecated exclues
// (permanent) puis masquage DEV (jetable). Voir appListing.ts.
import { useListedAppEntries } from './appListing.js';

// [MENU DEV] La ligne de version n'existe qu'en dev local. Meme couple que le
// menu DEV dans HomePanel.tsx : drapeau LITTERAL import.meta.env.DEV + import()
// dynamique. Au build de production la branche disparait, et avec elle le chunk
// de la bande. Ne JAMAIS remplacer import.meta.env.DEV par un helper importe,
// une variable intermediaire ou un optional chaining : c'est cette forme
// textuelle exacte que Vite remplace par une constante, et c'est ce
// remplacement qui permet a esbuild de supprimer la branche.
// A NE PAS CONFONDRE avec le TAMPON de build (/taki-build.json), qui est
// permanent et existe en production : voir tools/build-stamp/README.md.
const VersionBand = import.meta.env.DEV ? lazy(() => import('./dev/VersionBand.js')) : null;

// [STUDIO] LE STUDIO D'UNE APP, A DEUX REGIMES ET A UNE SEULE ADRESSE.
//
// `/studio/<appId>` EXISTE AUSSI EN PRODUCTION, et c'est le chantier
// `organisation-de-travail` : un client y personnalise SA declaration
// (`studio/instance/StudioInstanceScreen.tsx`), et le hub Parametres ne fait
// plus que l'y envoyer. Ce qui reste DEV-ONLY est l'atelier qui touche aux
// FICHIERS DU DEPOT : il n'a de sens que dans l'org master, et pas un octet
// n'en part chez un client.
//
// MEME COUPLE EXACT QUE LA BANDE CI-DESSUS : drapeau LITTERAL
// import.meta.env.DEV + import() dynamique. Au build de production, la branche
// dev disparait avec son chunk ; seul l'ecran d'instance reste.
// En dev, c'est l'aiguillage qui choisit, sur l'org de travail et sur rien
// d'autre (`dev/studio/StudioSelonRegime.tsx`).
const StudioAiguillage: ComponentType<StudioRouteProps> = import.meta.env.DEV
  ? lazy(() => import('./dev/studio/StudioSelonRegime.js'))
  : lazy(() => import('./studio/instance/StudioInstanceScreen.js'));

// [STUDIO][MENU DEV] LES DEUX ROUTES QUI CREENT UNE APP, sous leur garde. Elles
// ecrivent dans le depot : elles n'existent qu'en dev, et hors org master elles
// disent ou ce travail se fait (voir `StudioCreation`).
const StudioCreation = import.meta.env.DEV
  ? lazy(() =>
      import('./dev/studio/StudioSelonRegime.js').then((mod) => ({ default: mod.StudioCreation })),
    )
  : null;

const EVENEMENT_OPERATION_STUDIO = 'taki:studio-operation';

const AskTakiState = createContext<{ open: boolean; setOpen: (value: boolean) => void }>({
  open: false,
  setOpen: () => {},
});

function operationStudioMemorisee(): boolean {
  if (!import.meta.env.DEV) return false;
  try {
    return location.pathname.startsWith('/studio') && readStudioOperationMarker(sessionStorage) !== null;
  } catch {
    return false;
  }
}

type HoteMasqueStudio = HTMLDivElement & { __takiRoot?: Root };

function rendreMasqueStudio(active: boolean): HoteMasqueStudio | null {
  const existant = document.getElementById('taki-studio-operation-mask') as HoteMasqueStudio | null;
  if (!active) {
    /* Le layout effect qui reçoit la fin tourne pendant un commit React :
       démonter synchroniquement une AUTRE racine ici déclenche l'avertissement
       de concurrence de React. Le host visuel part tout de suite ; seule
       l'ancienne racine, capturée avant ce retrait, se démonte après le commit.
       Une nouvelle opération peut donc recréer un host sans être touchée. */
    const ancienneRacine = existant?.__takiRoot;
    existant?.remove();
    if (ancienneRacine !== undefined) queueMicrotask(() => ancienneRacine.unmount());
    return null;
  }
  const hote = existant ?? document.createElement('div') as HoteMasqueStudio;
  if (existant === null) {
    hote.id = 'taki-studio-operation-mask';
    hote.className = 'fixed inset-0 z-[100] overflow-hidden outline-none';
    hote.setAttribute('role', 'dialog');
    hote.setAttribute('aria-modal', 'true');
    hote.setAttribute('aria-label', 'Opération Studio en cours');
    hote.tabIndex = -1;
    document.body.append(hote);
    hote.__takiRoot = createRoot(hote);
    hote.__takiRoot.render(<BootScreen />);
  }
  return hote;
}

// Le lanceur rapide (E5) n'est plus instancie : le dock + le menu home
// overlay le remplacent. Le composant reste au chaud dans
// surfaces/QuickLauncher.tsx, reactivable si le besoin revient.

export interface TakiShellProps {
  /** Config runtime chargee via `@taki/runtime-config`. */
  config: TakiConfig;
  /**
   * Registre optionnel surchargeant les apps placeholder par defaut. Indexe
   * par id d'app ; toute app installee sans surcharge recoit un ecran
   * placeholder genere.
   */
  apps?: ShellAppRegistry;
}

/** Libelle editorial des sections du panneau Home (titre d'onglet). */
const HOME_SECTION_LABELS: Record<HomeSection, string> = {
  apps: 'Apps',
  account: 'Mon compte',
};

/** Id d'app extrait d'un pathname /app/<id>, sinon undefined. */
function appIdOf(pathname: string): string | undefined {
  return pathname.startsWith('/app/') ? pathname.split('/')[2] : undefined;
}

/**
 * LES PARAMETRES D'ADRESSE D'UNE APP, et rien d'autre.
 *
 * `view` la vue active, `id` l'element ouvert, `s` la section (l'onglet de la
 * zone 1), `f` la pastille de filtre, `arch` le mode Archives, `mod` le mode
 * Modeles. QUE DES IDENTIFIANTS : jamais un nom, jamais un email, jamais le
 * texte tape dans la recherche (voir `screenUrl.tsx` dans @taki/ui). Le contenu
 * de ces six champs appartient a l'ecran, pas au shell : `useScreen` les lit et
 * les ecrit tout seul.
 *
 * `arch` ET `mod` NE SONT PAS TYPES `string` COMME LES QUATRE AUTRES, et ce
 * n'est pas une negligence : voir `modeRouterValue` juste dessous. Ne les
 * "simplifie" pas.
 */
export interface AppSearch {
  view?: string;
  id?: string;
  s?: string;
  f?: string;
  arch?: string | number | boolean;
  /**
   * LE MODE MODELES. Meme type que `arch` PARCE QUE C'EST LE MEME PIEGE : sa
   * valeur d'allumage est `1`, donc du JSON valide, donc le routeur la
   * retourne. Le typer `string` le casserait exactement comme `arch` l'a ete.
   */
  mod?: string | number | boolean;
  /** La porte ouverte DANS une fiche parente (`porteDans.ts` de @taki/ui). */
  dans?: string;
  /** Le brouillon de vue HTML montre sur la fiche ouverte (chantier `fiche-suivi`). */
  apercu?: string;
  /**
   * LE LIEN « CREER UN FLUX » d'un smart bouton (`docs/socle/smart-boutons.md`) :
   * `/app/flux?nouveau=bouton&app=<app>&porte=<porte>&bouton=<id>`. L'app Flux ouvre
   * une automatisation neuve branchee sur ce bouton. Le shell ne fait que RELAYER ces
   * quatre cles, l'app les lit puis les efface de l'adresse.
   */
  nouveau?: string;
  app?: string;
  porte?: string;
  bouton?: string;
}

/**
 * LES PARAMETRES DE MODE, DANS LA FORME QUE LE ROUTEUR SAIT REIMPRIMER TELLE
 * QUELLE. Ce sont les seuls de nos parametres qui aient besoin de ca, et voici
 * pourquoi, parce que c'est invisible et que ca se "simplifie" tout seul a la
 * relecture suivante.
 *
 * ELLE S'APPELAIT `archRouterValue` ET ELLE A DEUX CLIENTS DEPUIS LE MODE
 * MODELES : le nom devait cesser de nommer un seul d'entre eux. Rien d'autre
 * n'a change dans son corps, et il ne fallait rien y changer : la fonction n'a
 * JAMAIS connu la clef qu'elle traite, seulement la forme de sa valeur. C'est
 * precisement ce qui lui permet de servir `mod` sans une ligne de plus.
 *
 * TanStack Router (aucun `parseSearch` ni `stringifySearch` installes ici,
 * donc ses defauts) fait passer chaque valeur d'adresse par du JSON, dans les
 * DEUX sens : a la lecture il rend `?arch=1` en NOMBRE `1`, et a l'ecriture il
 * re-serialise la chaine `'1'` en `arch=%221%22` (une chaine JSON-parsable est
 * re-stringifiee, guillemets compris). Aucun de nos quatre autres parametres
 * ne declenche ca : ce sont des ids (`core.liste`, `tous`, `actifs`), et rien
 * de tout ca n'est du JSON valide, donc ils traversent en chaines intactes.
 * `arch=1` a ete la premiere valeur d'adresse du produit qui soit du JSON
 * valide, et `mod=1` est la seconde : les deux modes s'allument par la meme
 * valeur, exactement pour ne pas avoir deux tolerances a tenir. Consequences si
 * on les traite comme les quatre autres : le mode ne peut ni entrer dans
 * l'adresse (elle affiche `arch=%221%22`) ni en etre relu (le nombre `1` n'est
 * pas une chaine, il tombe), sans la moindre erreur.
 *
 * La reponse est de DEFAIRE la conversion du routeur, sans jamais connaitre la
 * valeur qui allume le mode : ce fait-la vit a UN seul endroit par mode
 * (`ARCHIVES_ON` et `MODELES_ON` dans `screenUrl.tsx`), et le recopier ici
 * serait un fait ecrit deux fois. On rend donc au routeur la forme JSON de son
 * texte quand il y en a une (`'1'` devient le nombre `1`, qu'il reimprime
 * `arch=1`), et le texte tel quel sinon. La symetrie est exacte, dans les deux
 * sens, quelle que soit la valeur, et c'est ce qui rend la fonction partageable
 * telle quelle.
 */
function modeRouterValue(value: unknown): string | number | boolean | undefined {
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value !== 'string' || value === '') return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    // Les scalaires seulement : un objet ou un tableau n'a aucun sens dans nos
    // parametres, et il ne se relirait pas en texte.
    if (typeof parsed === 'number' || typeof parsed === 'boolean') return parsed;
  } catch {
    /* Pas du JSON : c'est du texte ordinaire, il traverse tel quel. */
  }
  return value;
}

/**
 * Validation des parametres d'adresse. Une adresse est du texte venu de
 * l'exterieur (un lien colle, un favori vieux de six mois, une faute de
 * frappe) : on ne garde que des chaines non vides, et une valeur absurde
 * disparait au lieu de traverser jusqu'a l'ecran.
 */
function validateAppSearch(search: Record<string, unknown>): AppSearch {
  const text = (key: string): string | undefined => {
    const value = search[key];
    return typeof value === 'string' && value !== '' ? value : undefined;
  };
  const validated: AppSearch = {};
  const view = text('view');
  const id = text('id');
  const section = text('s');
  const filter = text('f');
  const archives = modeRouterValue(search['arch']);
  const modeles = modeRouterValue(search['mod']);
  const dans = text('dans');
  const apercu = text('apercu');
  // Les cles absentes ne sont pas posees a `undefined` : le routeur ecrirait
  // sinon `?view=&id=` dans la barre d'adresse.
  if (view != null) validated.view = view;
  if (id != null) validated.id = id;
  if (section != null) validated.s = section;
  if (filter != null) validated.f = filter;
  if (archives != null) validated.arch = archives;
  if (modeles != null) validated.mod = modeles;
  if (dans != null) validated.dans = dans;
  if (apercu != null) validated.apercu = apercu;
  for (const cle of ['nouveau', 'app', 'porte', 'bouton'] as const) {
    const valeur = text(cle);
    if (valeur != null) validated[cle] = valeur;
  }
  return validated;
}

/** L'unique parametre d'adresse du hub Parametres : l'onglet ouvert. */
export interface SettingsSearch {
  /** Id de l'onglet (`dock`, `environnement`, `membres`, `conversations`, `app:<id>`...). */
  t?: string;
}

/**
 * Meme prudence que `validateAppSearch` : une adresse vient de l'exterieur.
 * Un `t` absurde n'est pas une erreur, il tombe, et le hub retombe sur `dock`
 * tout seul (son repli existe deja pour les apps masquees). Une cle RENOMMEE
 * (`?t=ia`, devenue `conversations`) est traduite ici, donc un ancien lien
 * ouvre le bon onglet (`ongletsParametres.ts`).
 */
function validateSettingsSearch(search: Record<string, unknown>): SettingsSearch {
  const onglet = search['t'];
  return typeof onglet === 'string' && onglet !== '' ? { t: cleDOnglet(onglet) } : {};
}

/**
 * L'adresse telle que l'ECRAN la lit : rien que des chaines
 * (`pickScreenSearch` de @taki/ui ne garde que ca, et `readArchivesParam` /
 * `readModelesParam` y attendent le TEXTE du parametre, pas la forme JSON du
 * routeur). C'est la seconde moitie de `modeRouterValue` : on rend a `arch` et
 * a `mod` le texte qu'ils avaient dans la barre d'adresse, et c'est l'ecran,
 * seul, qui decide de ce qu'ils signifient : l'exclusivite des deux modes se
 * tranche la-bas (`readModesParams`), jamais ici : le shell RELAIE, il ne lit
 * pas.
 */
function screenParamsOf(search: AppSearch): ScreenSearch {
  const params: ScreenSearch = {};
  if (search.view != null) params['view'] = search.view;
  if (search.id != null) params['id'] = search.id;
  if (search.s != null) params['s'] = search.s;
  if (search.f != null) params['f'] = search.f;
  if (search.arch != null) params['arch'] = String(search.arch);
  if (search.mod != null) params['mod'] = String(search.mod);
  if (search.dans != null) params['dans'] = search.dans;
  if (search.apercu != null) params['apercu'] = search.apercu;
  for (const cle of ['nouveau', 'app', 'porte', 'bouton'] as const) {
    const valeur = search[cle];
    if (valeur != null) params[cle] = valeur;
  }
  return params;
}

/**
 * Construit l'arbre de routes pour une config + des entrees d'apps resolues.
 *
 * Layout :
 *   __root__  →  ThemeProvider + AuthGuard + (surface de travail | bureau) + overlays
 *     /             →  bureau : panneau Home (section = etat interne du panneau)
 *     /account      →  bureau, section "Mon compte" preselectionnee
 *     /store        →  bureau, section "App Store" preselectionnee
 *     /settings     →  hub Parametres (meme layout que les apps : dock + plein ecran)
 *     /app/$appId   →  app installee montee paresseusement
 */
function buildRouter(config: TakiConfig, appEntries: ShellAppEntry[]) {
  const entriesById = new Map(appEntries.map((entry) => [entry.id, entry]));

  // Composants d'app RESOLUS (id -> export default du module). Les modules
  // sont aujourd'hui importes statiquement par le registre (bundle unique) :
  // on les resout tous au montage du shell et la navigation rend directement
  // le composant. Le montage "lazy" du routeur suspendait la route a CHAQUE
  // changement d'app et affichait une surface vide quelques frames : le
  // flash blanc constate par le fondateur.
  const resolvedApps = new Map<string, ComponentType>();

  const rootRoute = createRootRoute({
    component: function ShellLayout() {
      const navigate = useNavigate();
      const router = useRouter();
      const pathname = useRouterState({ select: (s) => s.location.pathname });

      // Le routeur ne type pas la recherche a la racine (chaque route a la
      // sienne) : on la prend en sac de cles brutes et c'est `validateAppSearch`
      // qui la ramene a nos quatre parametres.
      const search = useRouterState({
        select: (s) => s.location.search as Record<string, unknown>,
      });

      /*
       * LE BRANDING DE L'ENVIRONNEMENT, lu par le PORT et plus par la config.
       *
       * Le port rend la fusion « ce que le client a choisi par-dessus ce que le
       * deploiement dit » ; sans port (shell monte seul, tests), il rend `null`
       * et tout retombe sur `config.branding`, exactement comme avant ce
       * chantier. La config, elle, ne change JAMAIS : `buildRouter` reconstruit
       * son routeur sur l'identite de `config`, donc une copie modifiee
       * remonterait tout l'arbre a chaque frappe de l'ecran Environnement.
       */
      const branding = useBrandingPort();
      const nomEnvironnement = branding?.courant.name ?? config.branding.name;
      /* L'ICONE DE L'ENVIRONNEMENT : celle du client quand il en a choisi une,
         celle du deploiement sinon, et `null` quand il n'y en a aucune (le
         chevron Taki tient alors la place). Elle sert a DEUX endroits, le haut
         du dock et l'onglet du navigateur, et c'est la meme valeur : deux
         calculs finiraient par montrer deux marques differentes. */
      const iconeEnvironnement = branding?.courant.icon ?? config.branding.iconUrl ?? null;
      /* L'ACCENT SEUL VIENT DU PORT : `background` et `foreground` restent des
         surcharges de deploiement, que le client ne choisit pas. Sans port, on
         passe l'objet de la config TEL QUEL, parce qu'il accepte `rgb()` et
         `hsl()` la ou le port ne connait que le hex. */
      const couleurs = useMemo(
        () =>
          branding === null
            ? config.branding.colors
            : { ...config.branding.colors, primary: branding.courant.accent },
        [branding, config.branding.colors],
      );

      // L'etat vit au-dessus du routeur : changer d'app peut remonter son
      // layout racine, mais ne doit jamais replier la conversation ouverte.
      const { open: askAIOpen, setOpen: setAskAIOpen } = useContext(AskTakiState);
      // Menu home : le panneau Home complet en overlay par-dessus la surface
      // de travail (comportement standard, ouvert par le logo Taki du dock).
      // Ouverture/fermeture animees via la norme du socle (useOverlayMotion) :
      // a la fermeture, le panneau reste monte le temps de l'animation de
      // sortie (homeMenu.closing) puis est demonte.
      const [homeMenuOpen, setHomeMenuOpen] = useState(false);
      const homeMenu = useOverlayMotion(homeMenuOpen);
      const [operationStudio, setOperationStudio] = useState(operationStudioMemorisee);
      const [liaScreenContext, setLiaScreenContext] = useState<{ appId: string; doorId: string; period: { start: string; endExclusive: string }; personId?: string } | null>(null);
      const [liaScreen, setLiaScreen] = useState<LiaScreenState | null>(null);
      const masqueStudioRef = useRef<HTMLDivElement | null>(null);


      useLayoutEffect(() => {
        const update = (event: Event): void => {
          const detail = (event as CustomEvent<typeof liaScreenContext>).detail;
          setLiaScreenContext(detail);
        };
        window.addEventListener('taki:lia:screen-context', update);
        return () => window.removeEventListener('taki:lia:screen-context', update);
      }, []);
      // Ce que le moteur d'ecran publie (porte, vue, recherche, filtres, fiche ouverte) : toutes les apps.
      useLayoutEffect(() => {
        // Un etat identique au precedent ne re-rend pas la racine du shell.
        const update = (event: Event): void => {
          const next = (event as CustomEvent<LiaScreenState | null>).detail;
          setLiaScreen((previous) => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
        };
        window.addEventListener(LIA_SCREEN_EVENT, update);
        return () => window.removeEventListener(LIA_SCREEN_EVENT, update);
      }, []);

      useEffect(() => {
        if (!import.meta.env.DEV) return;
        const suivre = (event: Event): void => {
          setOperationStudio((event as CustomEvent<{ active?: boolean }>).detail?.active === true);
        };
        window.addEventListener(EVENEMENT_OPERATION_STUDIO, suivre);
        return () => window.removeEventListener(EVENEMENT_OPERATION_STUDIO, suivre);
      }, []);

      useLayoutEffect(() => {
        const racine = document.getElementById('root');
        const masque = rendreMasqueStudio(operationStudio);
        if (!operationStudio || racine === null || masque === null) return;
        racine.setAttribute('inert', '');
        masqueStudioRef.current = masque;
        masque.focus();
        return () => racine.removeAttribute('inert');
      }, [operationStudio]);

      // Surface de travail PLEIN ECRAN (dock + contenu) : une app (/app/...)
      // ou le hub Parametres (/settings). Sinon on est sur le bureau (la
      // fenetre flottante Home).
      // [STUDIO] `/studio` compte comme surface de travail : sans lui l'atelier
      // se rendrait dans la branche bureau, sans dock et avec le panneau Home
      // flottant par-dessus.
      const inApp =
        pathname.startsWith('/app/') ||
        pathname.startsWith('/settings') ||
        pathname.startsWith('/journal') ||
        pathname.startsWith('/studio');
      const appIdInPath = appIdOf(pathname);

      /*
       * LA MEMOIRE D'ADRESSE PAR APP, et sa raison n'est pas le confort.
       *
       * Sans elle, ouvrir une app se fait en DEUX temps VISIBLES : le routeur
       * pose d'abord `/app/<id>` tout nu (il navigue avant que l'ecran cible
       * n'existe), puis l'ecran se monte et ecrit ses parametres. La barre
       * d'adresse scintille, un etat intermediaire s'affiche une dizaine de
       * millisecondes. Aucun reglage d'effet ne rattrape ca, et ce n'est pas
       * faute d'avoir essaye : tant que la cible n'est pas connue AU CLIC, il y
       * a deux ecritures d'adresse.
       *
       * On retient donc l'adresse de chaque app visitee, et le dock y retourne
       * directement : l'ecran se monte sur une adresse qui dit deja tout, et il
       * n'a plus rien a reecrire. Une seule ecriture, aucun clignotement.
       *
       * Ce n'est PAS un changement de comportement : `useScreen` restaure deja
       * la vue et la fiche d'une app qu'on avait quittee (sessionStorage).
       * L'adresse se contente enfin de dire la meme chose que ce que l'ecran
       * allait montrer de toute facon.
       *
       * Memoire de PAGE (un F5 la vide, elle ne se partage pas) : elle ne
       * remplace pas la reprise de session, elle la suit. Premiere ouverture
       * d'une app dans la page : rien de memorise, donc l'ecran ecrit lui-meme,
       * comme avant. C'est la seule fois.
       */
      const lastSearchRef = useRef(new Map<string, AppSearch>());
      useEffect(() => {
        if (appIdInPath == null) return;
        lastSearchRef.current.set(appIdInPath, validateAppSearch(search));
      }, [appIdInPath, search]);
      const searchOf = (appId: string): AppSearch => lastSearchRef.current.get(appId) ?? {};

      // [MENU DEV] Le masquage vit dans les composants qui AFFICHENT, jamais
      // dans la cloture du routeur : entriesById et resolvedApps gardent la
      // liste complete, sinon masquer l'app ouverte la ferait tomber sur
      // "App introuvable".
      /*
       * LES MOTS DE L'ORG PASSENT AVANT CEUX DU BUNDLE, ET A UN SEUL ENDROIT :
       * ici. Une app que le client a renommee dans Parametres (chantier
       * `app-studio-client`) porte son nom et son icone dans SA declaration ;
       * `useEntreesEffectives` les pose sur les entrees de registre, et tous
       * les lecteurs d'en dessous (dock, menu home, hub, titre de l'onglet)
       * continuent de lire `entry.label` sans rien savoir des instances. Sans
       * instance, la liste rendue est CELLE qu'on a recue, a l'identite pres.
       *
       * ET AVANT LES MOTS, LA LISTE DE L'ORG : `appEntries` porte tout ce que
       * le bundle sait servir, `useEntreesInstallees` n'en garde que ce que
       * l'org a installe (`orgs/{orgId}/settings/apps`), dans son ordre.
       */
      const entreesInstallees = useEntreesInstallees(appEntries);
      const entreesEffectives = useEntreesEffectives(entreesInstallees);
      const entreesEffectivesById = useMemo(
        () => new Map(entreesEffectives.map((entry) => [entry.id, entry])),
        [entreesEffectives],
      );
      const visibleAppEntries = useListedAppEntries(entreesEffectives);
      const declarations = useDeclarationsPort();
      const ecransDeLApp = appIdInPath == null ? undefined
        : declarations?.instances.get(appIdInPath)?.ecrans ?? entreesEffectivesById.get(appIdInPath)?.declaration?.ecrans;
      const ecranCourant = (ecransDeLApp as { ecrans?: readonly [{ portes?: readonly { id: string; label: string }[]; porteArrivee?: string }] } | undefined)?.ecrans?.[0];
      const idPorteDemandee = typeof search['s'] === 'string' ? search['s'] : undefined;
      const porteCourante = ecranCourant?.portes?.find((porte) => porte.id === idPorteDemandee)
        ?? ecranCourant?.portes?.find((porte) => porte.id === ecranCourant.porteArrivee)
        ?? ecranCourant?.portes?.[0];
      // Le moteur d'ecran fait foi pour la porte affichee, dans toutes les apps (declarees ou codees).
      const ecranLia = liaScreen != null && liaScreen.appId === appIdInPath ? liaScreen : null;

      /* UNE APP NOUVELLEMENT INSTALLEE PREND SA PLACE DANS LE DOCK, meme quand
         le dock a ete personnalise (sinon la pref est une liste fermee et
         l'app n'apparait jamais). Monte ICI, dans le composant de la route
         racine : c'est le seul endroit rendu sur TOUTES les surfaces, bureau
         compris, alors que le dock lui-meme ne l'est que dans une app.
         Raisonnement complet : `dockAutoPin.ts`. */
      useDockAutoPin(entreesInstallees, visibleAppEntries);

      // Section du panneau Home preselectionnee par la route : la route "/"
      // porte tout (la section est un etat interne du panneau), /account reste
      // fonctionnelle en preselectionnant.
      const homeSection: HomeSection = pathname.startsWith('/account') ? 'account' : 'apps';

      // Titre de l'onglet : "<nom de l'environnement> • <la ou on est>". Dans une app, son nom du
      // registre ; sur /settings, le hub ; ailleurs, la section du bureau. Le
      // titre statique de l'index.html ne sert que le temps du premier rendu.
      // [STUDIO] Le titre de l'atelier se calcule A PART, et pas en elargissant
      // `appIdOf`. Cette fonction alimente aussi `appIdInPath`, dont depend la
      // memoire d'adresse par app (`lastSearchRef` plus bas) : elargie, elle
      // ferait memoriser l'adresse du STUDIO comme derniere adresse de l'app,
      // et le dock y retournerait au lieu de rouvrir l'app.
      // `/studio/projet/<poignee>` ouvre un PROJET, pas une app : son deuxieme
      // segment est le mot `projet`, jamais un id d'app. Sans ce test, l'onglet
      // s'appellerait « Studio, projet ».
      const studioAppId =
        pathname.startsWith('/studio/') && !pathname.startsWith('/studio/projet/')
          ? pathname.split('/')[2]
          : undefined;
      const currentLabel = pathname.startsWith('/studio')
        ? studioAppId == null
          ? 'Studio'
          : `Studio, ${entreesEffectivesById.get(studioAppId)?.label ?? studioAppId}`
        : ((appIdInPath != null ? entreesEffectivesById.get(appIdInPath)?.label : undefined) ??
          (pathname.startsWith('/settings') ? 'Paramètres'
            : pathname.startsWith('/journal') ? 'Journal' : HOME_SECTION_LABELS[homeSection]));
      /* L'ONGLET DU NAVIGATEUR PORTE LE NOM DU CLIENT, pas « Taki » : c'est SON
         environnement, et c'est la seule chose qui distingue deux onglets
         ouverts sur deux instances. */
      useEffect(() => {
        document.title = `${nomEnvironnement} • ${currentLabel}`;
      }, [nomEnvironnement, currentLabel]);

      /* ET LE FAVICON SUIT, quand le client a choisi une icone : dans une barre
         d'onglets, c'est l'image qu'on vise, pas le texte. Trois precautions,
         chacune payee par un bug evite :

           1. ON REPOSE L'ANCIEN AU DEMONTAGE. Le `<link>` vit dans `index.html`
              et pointe le favicon Taki : sans restauration, un environnement
              qui retire son icone garderait l'ancienne jusqu'au rechargement.
           2. ON NE TOUCHE PAS AU `type`. Une data URL porte deja son type MIME ;
              laisser `type="image/svg+xml"` sur un PNG fait ignorer le favicon
              par certains navigateurs.
           3. AUCUNE ICONE, AUCUNE ECRITURE. Un environnement par defaut ne doit
              pas voir son `<link>` reecrit a l'identique a chaque navigation. */
      useEffect(() => {
        if (iconeEnvironnement === null) return;
        const lien = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
        if (lien === null) return;
        const ancienHref = lien.getAttribute('href');
        const ancienType = lien.getAttribute('type');
        lien.setAttribute('href', iconeEnvironnement);
        lien.removeAttribute('type');
        return () => {
          if (ancienHref === null) lien.removeAttribute('href');
          else lien.setAttribute('href', ancienHref);
          if (ancienType !== null) lien.setAttribute('type', ancienType);
        };
      }, [iconeEnvironnement]);

      /*
       * L'ICONE D'APP DU RAIL, et son retour.
       *
       * AVANT, elle retenait la derniere app visitee dans une ref et NAVIGUAIT
       * vers elle : chaque clic EMPILAIT une etape, et deux apps se renvoyaient
       * la balle sans fin (A -> B, retour vers A, qui retenait B, retour vers
       * B...). Le bouton Precedent du navigateur ne retrouvait jamais la
       * sortie. Elle vivait aussi dans une memoire : le F5 l'effacait.
       *
       * MAINTENANT, l'etape elle-meme sait par ou on est entre dans cette zone
       * (`tkEntree`, tamponne par l'historique, `historique.ts`). Revenir, c'est
       * RECULER jusqu'a l'etape quittee (`history.go`), sans en ajouter : la
       * pile redevient exactement ce qu'elle etait. Sans entree connue (lien
       * colle, F5 sur une etape sans entree, app entre-temps retiree), on
       * retombe sur le bureau, comme toujours.
       */
      const etatDeLaLocation = useRouterState({ select: (s) => s.location.state });
      const entree = entreeDeLEtat(etatDeLaLocation);
      const rangCourant = rangDeLEtat(etatDeLaLocation);
      // L'app quittee doit exister encore (registre) et ne pas etre l'app
      // courante ; sinon le retour renvoie au bureau ("Home"). [MENU DEV] elle
      // doit aussi etre VISIBLE : sans ca, le libelle "Retour a <app masquee>"
      // resterait affiche dans le chrome pendant une demo.
      const entreeApp = entree?.app;
      const entreeRang = entree?.rang;
      const backEntry =
        entreeApp != null && entreeApp !== appIdInPath
          ? visibleAppEntries.find((candidate) => candidate.id === entreeApp)
          : undefined;
      const peutReculer =
        entreeRang != null &&
        rangCourant != null &&
        entreeRang < rangCourant &&
        (entreeApp === null || backEntry != null);
      const screenNav = useMemo<ScreenNav>(
        () => ({
          onBack: () => {
            if (peutReculer && entreeRang != null && rangCourant != null) {
              router.history.go(entreeRang - rangCourant);
            } else {
              void navigate({ to: '/' });
            }
          },
          backLabel: backEntry != null ? `Retour à ${backEntry.label}` : 'Home',
        }),
        [backEntry, navigate, router, peutReculer, entreeRang, rangCourant],
      );

      /* LE DEPART VERS LE HUB PARAMETRES, et c'est le MEME geste depuis les deux
         montages du panneau Home (le bureau et l'overlay pose au-dessus d'une
         app) : le panneau ne connait pas le routeur, le shell navigue et ferme
         l'overlay, exactement comme pour `pickApp`.
         AUCUN ONGLET N'EST FORCE : le hub s'ouvre sur son onglet par defaut
         (Dock) et son adresse reste `/settings` tout court, donc le retour du
         gabarit (`screenNav`) ramene la ou on etait, app comprise. */
      function ouvrirParametres(): void {
        setHomeMenuOpen(false);
        void navigate({ to: '/settings' });
      }

      function pickApp(appId: string): void {
        setHomeMenuOpen(false);
        // DEJA DANS CETTE APP : on ne navigue pas. Une navigation vers la meme
        // route repartirait d'une adresse SANS parametres (le routeur ne
        // reconduit pas la recherche), donc l'ecran se verrait retirer sa
        // fiche ouverte, sa section et sa pastille, et une etape d'historique
        // serait brulee au passage. Le clic sur l'icone de l'app active, ou le
        // faux clic, ne doit rien casser. Meme garde que `pickShortcut`.
        if (appId === appIdInPath) return;
        // On repart de l'adresse memorisee : l'ecran cible se monte sur une
        // adresse complete, il n'a rien a reecrire, la barre ne scintille pas.
        const goToApp = (): Promise<void> =>
          navigate({ to: '/app/$appId', params: { appId }, search: searchOf(appId) });
        // Depuis le BUREAU (fenetre Home sur fond clair), l'arrivee dans
        // l'app se fait en crossfade natif. On appelle
        // document.startViewTransition NOUS-MEMES plutot que par l'option
        // viewTransition du routeur (drapeau interne consomme au mauvais
        // moment : aucun fondu visible) : le navigateur fige l'ecran courant,
        // la navigation s'execute dans le callback (DOM a jour a la
        // resolution de sa promesse, les stores du routeur committent en
        // synchrone), puis il fond entre les deux images (CSS
        // ::view-transition-* de tokens.css). Depuis une app, le changement
        // reste INSTANTANE (doctrine : pas de fondu d'entree sur la surface
        // de travail, le menu overlay porte deja son propre fondu de sortie).
        // Navigateur sans support : bascule directe, comme avant.
        if (!inApp && typeof document.startViewTransition === 'function') {
          /* Une transition ABANDONNEE (onglet masque, transition suivante deja
             lancee) rejette `ready` : sans ce rattrapage, chaque abandon fait un
             « Uncaught (in promise) InvalidStateError » en console. La bascule
             elle-meme a bien eu lieu, seul le fondu manque. */
          const transition = document.startViewTransition(() => goToApp());
          transition.ready.catch(() => undefined);
          transition.finished.catch(() => undefined);
        } else {
          void goToApp();
        }
      }

      // Action de dock en attente : posee au clic d'un shortcut, consommee
      // par l'app visee (usePendingAppAction) qui ouvre son Modal puis
      // appelle consume() ; le shell la remet alors a null.
      const [pendingAction, setPendingAction] = useState<PendingAppAction | null>(null);
      const pendingActionValue = useMemo(
        () => ({ pending: pendingAction, consume: () => setPendingAction(null) }),
        [pendingAction],
      );

      function pickShortcut(appId: string, actionId: string): void {
        /*
         * UN RACCOURCI DE PORTE NOMME SA PORTE, et c'est le moteur d'ecran qui
         * y passe puis y ouvre la creation (`changerDePorte`,
         * `usePendingDoorAction`) : l'app n'a rien a ecrire pour ca. Depuis une
         * AUTRE app, l'adresse nomme deja la porte, pour que l'ecran se monte
         * directement dessus. Dans l'app ouverte, on ne touche PAS l'adresse :
         * elle arriverait apres l'ouverture du brouillon et le refermerait.
         */
        const porte = entreesEffectivesById
          .get(appId)
          ?.actions?.find((action) => action.id === actionId)?.porte;
        if (porte !== undefined) {
          setPendingAction({ appId, actionId: porte.actionId, door: porte.id, changerDePorte: true });
          if (appId !== appIdInPath) {
            void navigate({
              to: '/app/$appId',
              params: { appId },
              search: validateAppSearch({ s: porte.id }),
            });
          }
          return;
        }
        setPendingAction({ appId, actionId });
        // Deja dans l'app visee : poser l'action suffit, l'ecran monte la
        // consomme sans navigation.
        if (appId !== appIdInPath) {
          void navigate({ to: '/app/$appId', params: { appId }, search: searchOf(appId) });
        }
      }

      // « Ask Lia » (menu home, lanceur rapide) est le LANCEUR de Lia, comme la pastille du dock :
      // il ouvre Lia (dernier sujet, sinon sujet neuf), et ferme seulement si Lia est deja a l'ecran
      // (`gesteDuLanceur`, decision du 05/10/2026). Ctrl Maj L rouvre la derniere source vue.
      function openAskAI(): void {
        setHomeMenuOpen(false);
        ouvrirCentre({ source: 'lia', lanceur: true });
      }

      useDisplayScale();

      // Pre-resolution de tous les ecrans d'app au boot : la navigation entre
      // apps est ensuite instantanee (aucune suspension, aucun flash blanc).
      useEffect(() => {
        for (const entry of appEntries) {
          if (resolvedApps.has(entry.id)) continue;
          void entry.load().then((mod) => {
            resolvedApps.set(entry.id, mod.default);
          });
        }
        // appEntries est une constante de cloture du routeur : une seule passe.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      return (
        <ThemeProvider colors={couleurs}>
          <AuthGuard config={config}>
            {/* Les brouillons de la personne enveloppent les apps ET le centre. */}
            <BrouillonsDuCompte orgId={config.orgId}>
            {/* Barre de progression de navigation : rendue une fois l'auth
                resolue (dans le sous-arbre protege), au-dessus de tout. Elle
                se pilote seule via l'etat du routeur. */}
            <TakiProgressBar />
            {/* CONTENEUR EN COLONNE. Il porte les bandes qui se posent au-dessus
                de la surface de travail. Il ne suffisait PAS d'ajouter un enfant :
                le <main> ci-dessous est une RANGEE flex, une bande s'y serait
                posee A COTE du dock. La colonne porte donc la hauteur pleine
                (h-full : celle que lui laisse le `CadreEnvironnement` de l'hote,
                sous la bande STAGING quand elle est allumee, voir
                ./StagingBand.tsx), chaque bande prend sa rangee (shrink-0) et
                les deux branches ci-dessous se partagent le reste (min-h-0 flex-1).
                IL EST PERMANENT, et il ne l'a pas toujours ete : il est arrive
                avec la ligne de version, qui est un outil de DEV, mais la bande
                "app deprecated" ci-dessous existe en PRODUCTION et en depend.
                Retirer la greffe DEV ne retire donc PAS ce conteneur, ni les
                classes de hauteur qui vont avec (`min-h-0 flex-1` sur le <main>
                et sur le bureau).
                COUT CONNU de ce conteneur : il ne defile pas. Une bande allumee
                sur une fenetre basse (moins de ~34 rem de haut, environ 540 px)
                ecrete donc le haut et le bas de la fenetre Home, sans recours. Ca
                ne concerne que la ligne de version, qui est en dev et peut
                s'eteindre : la bande deprecated, elle, ne se rend QUE dans une
                app, jamais au-dessus du bureau, precisement pour ne pas exposer un
                client a cet ecretement. On ne corrige PAS en rabotant la hauteur
                du panneau Home : elle est validee par le fondateur. Le seuil est
                annonce dans dev/README.md section 3ter. */}
            <div className="flex h-full w-full flex-col overflow-hidden">
              {/* [MENU DEV] Bande a retirer en entier, avec son Suspense. Le
                  fallback est `null` : une bande d'information n'a pas de raison
                  de faire clignoter un squelette en haut de l'ecran. */}
              {VersionBand != null && (
                <Suspense fallback={null}>
                  <VersionBand config={config} />
                </Suspense>
              )}
              {inApp ? (
                <>
                  {/* Bande PERMANENTE (elle existe en production, contrairement a
                      la ligne de version) : l'app ouverte est-elle deprecated ?
                      Montee ICI, dans la seule branche `inApp`, pour deux
                      raisons : hors d'une app il n'y a rien a signaler, et le
                      conteneur en colonne ne defile pas, donc une bande au-dessus
                      du bureau ecreterait la fenetre Home sur un ecran bas. */}
                  <DeprecatedBand appId={appIdInPath} />
                  {/* Surface de travail : PLEIN ECRAN, dock a gauche. Le conteneur
                   donne la hauteur et ne defile pas : c'est le gabarit
                   ScreenLayout (h-full, zone 5 scrollable) qui gere son propre
                   scroll. [MENU DEV] `min-h-0 flex-1` remplace `h-screen` : la
                   hauteur pleine est passee au conteneur en colonne ci-dessus. */}
                  <main className="flex min-h-0 w-full flex-1 overflow-hidden bg-bg">
                  <Dock
                    appEntries={visibleAppEntries} // [MENU DEV] remettre entreesEffectives
                    activeAppId={appIdInPath}
                    onPickApp={pickApp}
                    onPickShortcut={pickShortcut}
                    onOpenHomeMenu={() => setHomeMenuOpen(true)}
                    iconeClient={iconeEnvironnement}
                    onOpenSettings={() => void navigate({ to: '/settings' })}
                    renderCloche={(dock) => <ClocheNotifications {...dock} />}
                  />

                  <FondCadreProvider>
                    {/* Le shell fournit onBack/backLabel aux ecrans (apps ET hub
                        /settings) : la puce d'icone du gabarit devient un bouton
                        retour vers la derniere app visitee, sinon le bureau. */}
                    <ScreenNavProvider value={screenNav}>
                      <PendingActionProvider value={pendingActionValue}>
                        {/* Changement d'app INSTANTANE : pas de fondu d'entree ni
                            de remontage force (cle) sur la surface de travail. Le
                            fondu global partait de opacity 0 a chaque navigation,
                            donc l'ecran repartait du transparent = un flash. Le
                            rail apparait direct ; seule la zone 5 fait un skeleton
                            si les donnees tardent vraiment (> 300ms). */}
                        <div className="h-full">
                          <Outlet />
                        </div>
                      </PendingActionProvider>
                    </ScreenNavProvider>
                  </FondCadreProvider>
                  </main>
                </>
              ) : (
                /* Bureau. Centrage OPTIQUE : le panneau est remonte d'un cran
                   par rapport au centre geometrique (le vide sous une fenetre
                   flottante se lit plus leger que le vide au-dessus).
                   [MENU DEV] `min-h-0 flex-1` remplace `min-h-screen`, pour la
                   meme raison que le <main> ci-dessus : sans ce changement, la
                   bande plus une hauteur d'ecran pleine depassent le viewport. */
                <div className="flex min-h-0 w-full flex-1 items-center justify-center bg-bg p-4 pb-[5vh] sm:p-6 sm:pb-[5vh]">
                  <HomePanel
                    config={config}
                    appEntries={entreesEffectives}
                    onPickApp={pickApp}
                    onAskAI={openAskAI}
                    onOpenSettings={ouvrirParametres}
                    initialSection={homeSection}
                  />
                </div>
              )}
            </div>

            {/* Notch DESACTIVE : le dock le remplace. Rendu conserve
                ci-dessous (fichier surfaces/Notch.tsx garde), reactivable en
                retirant `false &&`. */}
            {false && inApp && !askAIOpen && (
              <Notch expanded={homeMenuOpen} onToggle={() => setHomeMenuOpen((o) => !o)} />
            )}

            {/* MENU HOME (comportement standard) : le panneau Home complet en
                overlay par-dessus la surface de travail. Clic sur le voile =
                fermer et revenir a l'app ; changer de section reste DANS
                l'overlay (etat interne du panneau, aucune navigation) ;
                choisir une app = naviguer + fermer. */}
            {inApp && homeMenu.mounted && (
              <div
                className={cn(
                  'fixed inset-0 z-40 flex items-center justify-center p-4 pb-[5vh] sm:p-6 sm:pb-[5vh]',
                  // Pendant la sortie, l'overlay devient transparent aux
                  // clics : l'app redessous est utilisable sans attendre la
                  // fin du fondu.
                  homeMenu.closing && 'pointer-events-none',
                )}
              >
                {/* Voile bouton : un clic N'IMPORTE OU hors du panneau ferme
                    (curseur neutre, pas de selection de texte). */}
                <button
                  type="button"
                  aria-label="Fermer le menu"
                  onClick={() => setHomeMenuOpen(false)}
                  className={cn(
                    'absolute inset-0 cursor-default select-none bg-scrim backdrop-blur-sm',
                    homeMenu.closing ? 'taki-anim-fade-out' : 'taki-anim-fade',
                  )}
                />
                {/* pointer-events-none : le wrapper de centrage laisse passer
                    les clics des cotes vers le voile ; seul le panneau
                    lui-meme (pointer-events-auto) les intercepte. */}
                <div
                  role="dialog"
                  aria-label="Menu"
                  className="pointer-events-none relative z-10 flex w-full justify-center"
                >
                  <HomePanel
                    config={config}
                    appEntries={entreesEffectives}
                    onPickApp={pickApp}
                    onAskAI={openAskAI}
                    onOpenSettings={ouvrirParametres}
                    className={cn(
                      homeMenu.closing ? 'taki-anim-pop-out' : 'taki-anim-pop pointer-events-auto',
                    )}
                  />
                </div>
              </div>
            )}

            <CentreCommunication
              destinationFiche={(family, type) => {
                const destination = destinationPour(visibleAppEntries.map((entry) => ({
                  entry, ecrans: declarations?.instances.get(entry.id)?.ecrans ?? entry.declaration?.ecrans,
                })), family, type);
                if (destination) return destination;
                // Les apps manuelles exposent leurs portes dans les actions du registre.
                for (const entry of visibleAppEntries) {
                  const porte = entry.actions?.find(action => action.porte?.jeu?.family === family
                    && action.porte.jeu.type === type)?.porte;
                  if (porte) return { app: entry.id, appLabel: entry.label, porte: porte.id };
                }
                return null;
              }}
              open={askAIOpen}
              onOpen={() => setAskAIOpen(true)}
              onClose={() => setAskAIOpen(false)}
              onOpenRecord={(record, options) => {
                if (appIdInPath !== record.appId || (porteCourante?.id ?? idPorteDemandee) !== record.doorId || search['id'] !== record.id) {
                  void navigate({
                    to: '/app/$appId',
                    params: { appId: record.appId },
                    search: validateAppSearch({ s: record.doorId, id: record.id }),
                    viewTransition: false,
                  });
                }
                if (!options?.garderChatOuvert && window.matchMedia('(max-width: 639px)').matches) setAskAIOpen(false);
              }}
              onOpenPlace={(place) => {
                void navigate({
                  to: '/app/$appId',
                  params: { appId: place.appId },
                  search: validateAppSearch(place.doorId ? { s: place.doorId } : {}),
                  viewTransition: false,
                });
                if (window.matchMedia('(max-width: 639px)').matches) setAskAIOpen(false);
              }}
              config={config}
              /* Ce que l'ecran montre, pour la colonne « Fiche suivie » et le composeur de Lia. */
              contexte={{
                ...(appIdInPath != null ? { appId: appIdInPath, appLabel: entreesEffectivesById.get(appIdInPath)?.label } : {}),
                ...((ecranLia?.doorId ?? porteCourante?.id ?? idPorteDemandee) ? { doorId: ecranLia?.doorId ?? porteCourante?.id ?? idPorteDemandee } : {}),
                ...((ecranLia?.doorLabel ?? porteCourante?.label) ? { doorLabel: ecranLia?.doorLabel ?? porteCourante?.label } : {}),
                ...(ecranLia?.recordId ? { recordId: ecranLia.recordId } : {}),
                ...(ecranLia?.recordTitle ? { recordTitle: ecranLia.recordTitle } : {}),
                ...(ecranLia?.family ? { family: ecranLia.family } : {}),
              }}
              /* Le contexte de Lia, SANS la fiche : c'est le sujet qui dit quelle fiche il suit. */
              contexteLia={{
                appId: appIdInPath ?? studioAppId,
                doorId: ecranLia?.doorId ?? porteCourante?.id ?? idPorteDemandee,
                ...(ecranLia ? {
                  ...(ecranLia.view ? { view: ecranLia.view } : {}),
                  ...(ecranLia.search ? { search: ecranLia.search } : {}),
                  ...(ecranLia.filters ? { filters: ecranLia.filters } : {}),
                  ...(ecranLia.archives ? { archives: true } : {}),
                } : {}),
                // Le serveur le remplace par le fuseau de l'org (`docs/socle/fuseau-org.md`).
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                ...(liaScreenContext != null && liaScreenContext.appId === appIdInPath && liaScreenContext.doorId === (ecranLia?.doorId ?? porteCourante?.id ?? idPorteDemandee)
                  ? { period: liaScreenContext.period, personId: liaScreenContext.personId } : {}),
              }}
              appEntries={entreesEffectives}
              onNavigate={(chemin) => void navigate({ href: chemin })}
            />
            </BrouillonsDuCompte>
          </AuthGuard>
        </ThemeProvider>
      );
    },
  });

  // Le bureau (panneau Home) est rendu par le layout racine : la section est
  // un ETAT INTERNE du panneau, ces trois routes ne servent que d'ancres
  // d'URL (et de preselection de section).
  const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: () => null,
  });

  const accountRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/account',
    component: () => null,
  });

  /*
   * L'APP STORE EST ETEINT, ET SON ADRESSE AUSSI.
   *
   * CE QUI CHANGE : plus aucune entree ne mene au catalogue (le rail du panneau
   * Home montre Apps, Mon compte, Parametres), et `/store` ne rend plus rien,
   * elle RENVOIE au bureau. Une route qui reste montable par un favori, un lien
   * colle ou l'historique laisserait une porte ouverte sur un catalogue qui
   * n'installe rien : c'est exactement la promesse qu'on retire.
   *
   * LE RENVOI SE FAIT EN `beforeLoad`, avant que quoi que ce soit ne soit monte,
   * et `replace` pour ne pas laisser dans l'historique une etape qui renvoie
   * (sans lui, le bouton Retour du navigateur reviendrait sur `/store`, donc sur
   * le renvoi, en boucle).
   *
   * CE QUI RESTE : l'ecran `screens/AppStoreScreen.tsx`, INTACT et plus importe
   * par personne. LE CHEMIN DE REACTIVATION, en entier : rendre l'ecran depuis
   * cette route (a la place du `beforeLoad`), et reposer l'entree de rail dans
   * `HomePanel.tsx` a cote de « Mon compte ». Rien d'autre, aucun drapeau,
   * aucune configuration.
   */
  const storeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/store',
    beforeLoad: () => {
      throw redirect({ to: '/', replace: true });
    },
    component: () => null,
  });

  // Hub Parametres : route shell (pas /app/*), rendue dans le meme layout que
  // les apps (dock visible + surface pleine).
  //
  // L'ONGLET A UNE ADRESSE (`?t=membres`), et pour la meme raison que l'etat
  // d'un ecran d'app : sans elle, « va dans Parametres, onglet Utilisateurs »
  // ne se dit qu'en francais, jamais en lien, et un F5 ramene sur le Dock.
  const settingsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/settings',
    validateSearch: validateSettingsSearch,
    component: function ShellSettings() {
      const search = settingsRoute.useSearch();
      const navigate = useNavigate();
      // [MENU DEV] Le hub recoit la liste COMPLETE, et filtre lui-meme ses
      // ONGLETS : son panneau Dock REECRIT les prefs dock.apps et
      // dock.shortcuts a partir de la liste qu'on lui donne. Filtrer ici
      // effacerait definitivement l'epinglage d'une app masquee, alors que le
      // masquage doit rester cosmetique et reversible.
      /* MEMES MOTS QUE LE DOCK : le rail du hub nomme les apps, et une app
         renommee par l'org doit y porter son nom, pas celui du bundle. */
      const entreesInstallees = useEntreesInstallees(appEntries);
      const entreesEffectives = useEntreesEffectives(entreesInstallees);
      /* L'onglet « Apps installees » propose TOUT le registre, sous les mots de l'org. */
      const catalogue = useEntreesEffectives(appEntries);
      return (
        <SettingsHubScreen
          appEntries={entreesEffectives}
          catalogue={catalogue}
          config={config}
          tab={search.t}
          /* LA PERSONNALISATION A DEMENAGE DANS LE STUDIO : l'onglet d'une app
             declaree n'en porte plus qu'une porte, et c'est le shell qui sait
             ou elle mene. */
          onOuvrirStudio={(id) => void navigate({ to: '/studio/$appId', params: { appId: id } })}
          onTabChange={(id) =>
            void navigate({
              to: '/settings',
              search: { t: id },
              // REMPLACE, jamais pousse : changer d'onglet n'est pas une
              // navigation, et trois onglets essayes rendraient le bouton
              // Retour du navigateur inutilisable.
              replace: true,
              viewTransition: false,
            })
          }
        />
      );
    },
  });

  /*
   * LE JOURNAL D'AUDIT : route CACHEE, absente du dock, du hub et du menu
   * (decision du fondateur, 04/10/2026). Cachee n'est pas protegee : seules les
   * rules ouvrent `auditEvents`, au proprietaire actif. L'ecran le dit a tout
   * autre compte (`screens/JournalScreen.tsx`, `docs/JOURNAL.md`).
   */
  const journalRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/journal',
    component: function ShellJournal() {
      const entreesInstallees = useEntreesInstallees(appEntries);
      const entreesEffectives = useEntreesEffectives(entreesInstallees);
      const apps = useMemo(() => entreesEffectives.map((e) => ({ id: e.id, label: e.label })), [entreesEffectives]);
      return <JournalScreen config={config} apps={apps} />;
    },
  });

  // Une route paresseuse par app installee. `lazyRouteComponent` differe le
  // chargement du bundle de l'app jusqu'a la visite de sa route (code-splitting).
  const appRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/app/$appId',
    validateSearch: validateAppSearch,
    component: function AppRouteComponent() {
      const { appId } = appRoute.useParams();
      const search = appRoute.useSearch();
      /* L'ADRESSE COURANTE, LUE PAR LES SAUTS ENTRE APPS au moment du clic (la
         fiche qu'on quitte) : un miroir, pour que le port de navigation ne
         change pas d'identite a chaque adresse. */
      const searchRef = useRef(search);
      searchRef.current = search;
      const navigate = useNavigate();
      const router = useRouter();
      const acces = useAccesPort();
      /* UNE APP NON INSTALLEE NE S'OUVRE PAS, meme par son adresse : le routeur
         connait tout le registre, la liste de l'org decide. */
      const installation = useInstallationPort();
      const installeeIci = useEntreesInstallees(appEntries).some((installee) => installee.id === appId);
      const entry = installeeIci ? entriesById.get(appId) : undefined;

      /*
       * LE PORT D'URL passe a l'ecran (contrat : @taki/ui, screenUrl.tsx).
       * C'est le seul endroit du shell qui sait que l'etat d'un ecran a une
       * adresse ; l'ecran, lui, ne connait ni le routeur ni cette route.
       *
       * `push` n'est vrai que pour l'OUVERTURE d'une fiche : c'est la seule
       * navigation interne a un ecran dont le Retour du navigateur doit
       * s'occuper. Changer de vue, de section ou de pastille REMPLACE l'etape,
       * sinon trois clics dans le selecteur rendent le bouton Retour
       * inutilisable.
       *
       * L'ORIGINE D'UNE FICHE VIT DANS L'ETAT DE L'ETAPE (`history.state`), pas
       * dans l'adresse ni dans une memoire (`historique.ts`) : on LIT donc cet
       * etat de facon reactive, et le port le rend a l'ecran (`origine`, `rang`).
       * Un REMPLACEMENT garde tout l'etat precedent (c'est la meme etape,
       * regardee autrement : deux etats egaux n'ecrivent rien) ; un PUSH repart
       * d'un etat neuf, auquel la decoration de l'historique ajoute l'entree
       * dans la zone.
       */
      const org = config.orgId ?? null;
      const etatDeLEtape = useRouterState({ select: (s) => s.location.state });
      const url = useMemo<ScreenUrlPort>(
        () => ({
          // L'ecran ne voit que du TEXTE : c'est `screenParamsOf` qui defait
          // la forme JSON dans laquelle le routeur rend `arch`.
          params: screenParamsOf(search),
          origine: lireOrigine(etatDeLEtape, org),
          rang: rangDeLEtat(etatDeLEtape),
          setParams: (next, options) => {
            const pousse = options?.push === true;
            void navigate({
              to: '/app/$appId',
              params: { appId },
              search: validateAppSearch(next),
              replace: !pousse,
              state: majEtat((prev) => {
                if (!pousse) {
                  // `undefined` = on ne touche pas ; `null` = on efface.
                  if (options?.origine === undefined) return { ...prev };
                  const { tkOrigine: _efface, ...reste } = prev;
                  return options.origine === null ? reste : { ...reste, tkOrigine: options.origine };
                }
                const tkOrigine =
                  options?.origine ??
                  (options?.depuis == null
                    ? undefined
                    : completerOrigine(options.depuis, {
                        app: appId,
                        rang: rangDeLEtat(prev),
                        org,
                        origineCourante: lireOrigine(prev, org),
                        paramsCourants: screenParamsOf(search),
                      }));
                return tkOrigine === undefined ? {} : { tkOrigine };
              }),
              // Pas de fondu sur un changement d'etat INTERNE a l'ecran : le
              // crossfade du routeur est fait pour passer d'un ecran a un
              // autre, pas pour ouvrir une fiche.
              viewTransition: false,
            });
          },
          back: () => router.history.back(),
          revenir: (origine, geste) => {
            if (geste === 'depiler') {
              router.history.back();
              return;
            }
            /* NAVIGUER REMPLACE L'ETAPE COURANTE par la fiche d'origine, dans SON
               app (la meme ou une autre : un seul code), et lui rend sa propre
               origine : le retour suivant sait encore d'ou elle venait. */
            void navigate({
              to: '/app/$appId',
              params: { appId: origine.app },
              search: validateAppSearch(origine.adresse),
              replace: true,
              state: majEtat(() => ({ tkOrigine: origine.parent ?? null })),
              viewTransition: false,
            });
          },
        }),
        [search, etatDeLEtape, org, navigate, router, appId],
      );

      /*
       * LE PORT DE NAVIGATION ENTRE APPS (contrat : @taki/ui, navigation.tsx).
       * Une reference vers une famille que l'ecran ne monte pas demande ici
       * « qui la montre ? » ; on repond parmi les apps que ce membre VOIT
       * (deprecated, acces et masquage dev deja retires par
       * `useListedAppEntries`), sur la declaration EFFECTIVE de chacune :
       * celle de l'org quand elle en a une, celle du bundle sinon, le meme
       * choix que `resoudreDeclaration` cote app-runtime. Ouvrir, c'est la
       * MEME adresse qu'une fiche ouverte a la main dans l'autre app
       * (`/app/<id>?s=<porte>&id=<fiche>`), en push : le Retour du navigateur
       * ramene a la fiche d'ou l'on vient, et la PUCE DE SORTIE de la fiche
       * d'arrivee aussi (l'origine est rangee dans l'etat de l'etape poussee).
       */
      const entreesInstallees = useEntreesInstallees(appEntries);
      const entreesEffectives = useEntreesEffectives(entreesInstallees);
      const entreesVisibles = useListedAppEntries(entreesEffectives);
      const instances = useDeclarationsPort()?.instances;
      const navigation = useMemo<NavigationPort>(() => {
        const candidates = entreesVisibles.map((entree) => ({
          entry: entree,
          ecrans: instances?.get(entree.id)?.ecrans ?? entree.declaration?.ecrans,
        }));
        return {
          destination: (family, type) => destinationPour(candidates, family, type),
          /* Parmi les MEMES apps visibles que `destination` : ouvrables par ce
             membre, leurs ecrans effectifs (ceux de l'org d'abord). */
          peutOuvrir: (app, porte) => porteOuvrable(candidates, app, porte),
          /* L'ORIGINE PART DANS L'ETAT DE L'ETAPE POUSSEE, et non dans une
             memoire : l'ecran de depart est DEMONTE par un saut entre apps, et
             celui d'arrivee n'a jamais rien su de lui ; l'etape, elle, survit
             aux deux, au F5 et a Precedent. Sans titre a dire (`depuis` nul),
             l'etape part sans origine : une puce muette n'existe pas. */
          ouvrir: (destination, id, depuis) => {
            void navigate({
              to: '/app/$appId',
              params: { appId: destination.app },
              search: validateAppSearch({ s: destination.porte, id }),
              state: majEtat((prev) =>
                depuis == null
                  ? {}
                  : {
                      tkOrigine: completerOrigine(
                        { porte: destination.porte, id, titre: depuis, adresse: screenParamsOf(searchRef.current) },
                        {
                          app: appId,
                          rang: rangDeLEtat(prev),
                          org,
                          origineCourante: lireOrigine(prev, org),
                          paramsCourants: screenParamsOf(searchRef.current),
                        },
                      ),
                    }),
            });
          },
          /* LE MEME SAUT, SANS FICHE : on passe dans la porte pour y CREER (le
             « Ajouter » d'un champ de lien). L'arrivee est la fiche VIDE, dont
             l'id est `BROUILLON_ID` : c'est lui que l'origine retient, et c'est
             sur lui que l'ecran d'arrivee la relit quand le brouillon s'ouvre. */
          ouvrirPorte: (destination, depuis) => {
            void navigate({
              to: '/app/$appId',
              params: { appId: destination.app },
              search: validateAppSearch({ s: destination.porte }),
              state: majEtat((prev) =>
                depuis == null
                  ? {}
                  : {
                      tkOrigine: completerOrigine(
                        { porte: destination.porte, id: BROUILLON_ID, titre: depuis, adresse: screenParamsOf(searchRef.current) },
                        {
                          app: appId,
                          rang: rangDeLEtat(prev),
                          org,
                          origineCourante: lireOrigine(prev, org),
                          paramsCourants: screenParamsOf(searchRef.current),
                        },
                      ),
                    }),
            });
          },
          iconeDApp: (id) => entreesVisibles.find((entree) => entree.id === id)?.icon,
          /* LE SUIVI D'UNE FICHE EN MOTS hors du centre (Canaux) : le catalogue
             de l'app que `destination` choisit, par les MEMES deux fonctions
             que le centre (`centre/suiviEnMots.ts`), declaration de l'org
             d'abord. Un ancien message a clefs s'y lit donc comme au centre. */
          catalogueDuSuivi: (family, type) => {
            const destination = destinationPour(candidates, family, type);
            return destination === null
              ? null
              : catalogueDeLaFiche(declarationDeLApp(destination.app, entreesVisibles, instances), family, type ?? null);
          },
        };
      }, [entreesVisibles, instances, navigate, appId, org]);

      // Rendu DIRECT depuis le cache des composants resolus : aucune
      // suspension au changement d'app. Repli si un module n'est pas encore
      // resolu a la premiere visite (futur vrai code-splitting) : on le
      // charge puis on force un re-rendu ; en attendant la surface reste
      // vide et la barre de progression du shell couvre l'attente.
      const [, bump] = useReducer((n: number) => n + 1, 0);
      const AppComponent = entry != null ? resolvedApps.get(entry.id) : undefined;
      useEffect(() => {
        if (entry == null || resolvedApps.has(entry.id)) return;
        let alive = true;
        void entry.load().then((mod) => {
          resolvedApps.set(entry.id, mod.default);
          if (alive) bump();
        });
        return () => {
          alive = false;
        };
      }, [entry]);

      if (entry == null) {
        // La liste n'est pas encore lue : la barre de progression du shell couvre l'attente.
        if (installation?.pret !== true) return null;
        return (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <h1 className="text-[1.375rem] font-semibold tracking-[-0.02em] text-ink-900">
              App introuvable
            </h1>
            <p className="text-sm text-ink-500">
              {`L'app "${appId}" n'est pas installée pour cette organisation.`}
            </p>
          </div>
        );
      }

      /*
       * L'APP EXISTE, MAIS PAS POUR CE COMPTE. Les listes la cachent deja
       * (`useListedAppEntries`), mais un favori, un lien colle ou l'historique
       * du navigateur amenent ici sans passer par elles : sans ce cas, l'app
       * se monterait et se ferait refuser une requete apres l'autre, sans que
       * l'ecran dise pourquoi. On ne le fait QUE quand la fiche est lue :
       * avant, on ne sait rien, et un refus sur une ignorance est un faux.
       * L'ecran DIT la raison (G16) ; ce n'est pas lui qui protege, ce sont
       * les rules.
       */
      if (acces !== null && acces.pret && acces.niveauApp(appId) === 'none') {
        return (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <h1 className="text-[1.375rem] font-semibold tracking-[-0.02em] text-ink-900">
              Acces refuse
            </h1>
            <p className="text-sm text-ink-500">
              {`${RAISONS_ACCES.appSansAcces} Demande à un administrateur d'ouvrir "${entry.label}" pour ton compte.`}
            </p>
          </div>
        );
      }

      if (AppComponent == null) return null;
      return (
        // `key={appId}` : le fournisseur d'adresse est REMONTE a chaque
        // changement d'app. Il tient l'arbitre "une adresse, un ecran" ; sans
        // ce remontage, l'arbitre d'une app survivrait a la suivante, qui se
        // verrait refuser l'adresse par un ecran deja parti (l'adresse se
        // figeait alors sur /app/<id> jusqu'au rechargement de la page). La
        // route, elle, ne se demonte pas quand seul le parametre change : c'est
        // bien ce remontage-ci qu'il faut demander, pas celui du composant.
        <ScreenUrlProvider key={appId} value={url}>
          <NavigationProvider value={navigation}>
            <AppComponent />
          </NavigationProvider>
        </ScreenUrlProvider>
      );
    },
  });

  /*
   * [STUDIO] LA ROUTE DU STUDIO D'UNE APP, EN PRODUCTION AUSSI.
   *
   * `/studio/$appId` edite UNE app existante, `/studio` en cree une neuve : le
   * parametre est optionnel cote adresse parce que TanStack traite `$appId`
   * comme requis, d'ou DEUX routes plutot qu'un schema de recherche a valider
   * (`validateAppSearch` est deja le morceau le plus delicat de ce fichier).
   *
   * CELLE-CI ENTRE TOUJOURS DANS L'ARBRE, et c'est le chantier
   * `organisation-de-travail` : une org y personnalise sa declaration, geste qui
   * ne touche que son propre document. Les deux autres (creation) restent
   * dev-only : elles ecrivent des fichiers du depot.
   *
   * ELLE EST DERRIERE LE MEME GARDE D'AUTH QUE `/settings` : les deux sont
   * enfants de `rootRoute`, dont le composant enveloppe tout l'arbre dans
   * `AuthGuard`. Le port des declarations, lui, vient de l'hote
   * (`DeclarationsProvider`, apps/hello/src/App.tsx), qui enveloppe le shell
   * entier : il est donc disponible sur `/studio/*` comme partout ailleurs.
   */
  const studioAppRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/studio/$appId',
    component: function ShellStudio() {
      const { appId } = studioAppRoute.useParams();
      const navigate = useNavigate();
      const entreesInstallees = useEntreesInstallees(appEntries);
      /* DEUX GESTES, UNE SEULE DESTINATION, ET `remplacer` LES DISTINGUE. Une
         app CONSTRUITE renvoie vers son projet AU MONTAGE : pousse, ce renvoi
         rendrait le bouton Retour inutilisable, puisqu'il ramenerait sur
         l'adresse qui renvoie. Un CLIC sur « Ouvrir le projet », lui, reste une
         entree d'historique. */
      return (
        <StudioAiguillage
          appId={appId}
          /* L'ORG DE TRAVAIL VIENT DE LA CONFIG RUNTIME, et de nulle part
             ailleurs : c'est elle que le SDK pose sur chaque ecriture, donc
             c'est elle que le Studio personnalise. */
          orgId={config.orgId}
          entries={entreesInstallees}
          onVoirApp={(id) => void navigate({ to: '/app/$appId', params: { appId: id } })}
          onAdresseProjet={(poignee, options) =>
            void navigate({
              to: '/studio/projet/$projetId',
              params: { projetId: poignee },
              replace: options?.remplacer === true,
            })
          }
        />
      );
    },
  });
  /*
   * [STUDIO] LA TROISIEME ADRESSE : un PROJET d'app en cours.
   *
   * POURQUOI UNE ADRESSE, et pas seulement une sauvegarde sur disque : sans
   * elle, un F5, un onglet ferme ou un plantage ramenent sur `/studio`,
   * c'est-a-dire sur un projet NEUF, et il faut aller rechercher le sien dans
   * la liste. Avec elle, l'adresse EST le projet : elle se met en signet, elle
   * se recharge, elle se partage entre deux fenetres de la meme machine.
   *
   * TROIS SEGMENTS CONTRE DEUX : aucune ambiguite avec `/studio/$appId`, qui
   * n'en prend qu'un. Un id d'app ne peut donc jamais valoir « projet ».
   *
   * ELLE RESTE DEV-ONLY, comme `/studio` : creer une app pose des FICHIERS DU
   * DEPOT, c'est-a-dire le modele que toutes les organisations recevront. Hors
   * org master, `StudioCreation` rend l'ecran de garde qui dit ou ce travail se
   * fait.
   */
  const studioProjetRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/studio/projet/$projetId',
    component: function ShellStudioProjet() {
      const { projetId } = studioProjetRoute.useParams();
      const navigate = useNavigate();
      return StudioCreation === null ? null : (
        <StudioCreation
          projetId={projetId}
          onAdresseProjet={(poignee) =>
            void navigate({ to: '/studio/projet/$projetId', params: { projetId: poignee } })
          }
          /* UN PROJET QUI A DEJA CREE SON APP doit pouvoir aller la VOIR : la
             seconde creation du meme nom sera refusee, et renommer l'app qu'on
             vient de reussir n'est pas la sortie. `/studio/<id>` l'est. */
          onOuvrirApp={(id) => void navigate({ to: '/studio/$appId', params: { appId: id } })}
          onVoirApp={(id) => void navigate({ to: '/app/$appId', params: { appId: id } })}
          onOuvrirOrganisations={() =>
            void navigate({ to: '/app/$appId', params: { appId: 'cockpit' }, search: { s: 'orgs' } })
          }
        />
      );
    },
  });
  const studioNeufRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/studio',
    component: function ShellStudioNeuf() {
      const navigate = useNavigate();
      return StudioCreation === null ? null : (
        <StudioCreation
          /* OUVRIR UN PROJET EST UN GESTE, donc une entree d'historique : le
             bouton Retour ramene sur le projet neuf qu'on avait sous les yeux.
             La naissance d'un projet, elle, ne change PAS l'adresse (voir
             `StudioScreen`) : ces deux routes sont deux composants, et le
             remontage renverrait au Dashboard en pleine frappe. */
          onAdresseProjet={(poignee) =>
            void navigate({ to: '/studio/projet/$projetId', params: { projetId: poignee } })
          }
          /* Meme sortie que sur un projet ouvert : un nom deja pris se lit dans
             le formulaire des la premiere frappe, et l'app qui le porte
             s'ouvre. */
          onOuvrirApp={(id) => void navigate({ to: '/studio/$appId', params: { appId: id } })}
          onVoirApp={(id) => void navigate({ to: '/app/$appId', params: { appId: id } })}
          onOuvrirOrganisations={() =>
            void navigate({ to: '/app/$appId', params: { appId: 'cockpit' }, search: { s: 'orgs' } })
          }
        />
      );
    },
  });
  /* LA ROUTE DU STUDIO D'UNE APP EST DE TOUS LES BUILDS ; les deux routes de
     CREATION n'entrent dans l'arbre qu'en dev, et pas seulement leur composant :
     une adresse declaree avec un composant vide resterait une porte a pousser
     chez un client. */
  const studioRoutes =
    StudioCreation === null
      ? [studioAppRoute]
      : [studioProjetRoute, studioAppRoute, studioNeufRoute];

  const routeTree = rootRoute.addChildren([
    indexRoute,
    accountRoute,
    storeRoute,
    settingsRoute,
    journalRoute,
    appRoute,
    ...studioRoutes,
  ]);

  return createRouter({
    routeTree,
    // L'HISTORIQUE DU NAVIGATEUR, depuis le chantier socle 1 (30/07/2026).
    // Avant, le shell montait un historique MEMOIRE : rien n'etait visible dans
    // la barre d'adresse, F5 ramenait au bureau, aucun lien n'etait
    // partageable et le bouton Retour ne faisait rien. Le prix a payer est un
    // prerequis d'hebergement, deja tenu : le serveur doit renvoyer
    // `index.html` sur TOUTE route, sinon un lien partage tombe en 404 (voir
    // `rewrites` dans firebase.json).
    history: decorerHistorique(createBrowserHistory()),
    defaultPreload: 'intent',
    // Les navigations du routeur restent instantanees, en particulier entre
    // deux apps : une transition globale conserve une couche au-dessus du DOM
    // et peut avaler le clic suivant. `pickApp` demarre explicitement le seul
    // crossfade autorise, depuis le bureau vers une app.
    defaultViewTransition: false,
  });
}

/**
 * Shell applicatif MyTaki.
 *
 * Lit les apps installees de l'org (port d'installation), rend le bureau (panneau Home : rail de
 * navigation + contenu de section), monte une app par route (composants
 * pre-resolus au boot : navigation instantanee) derriere un dock d'apps, heberge le hub Parametres (/settings), theme le
 * tout depuis le branding de l'environnement (le port de @taki/ui quand l'hote
 * en monte un, `config.branding` sinon), et garde toute la surface derriere un auth
 * guard (ecran de connexion quand aucun utilisateur n'est connecte). Les
 * overlays systeme (menu home, Lia) flottent au-dessus.
 */
export function TakiShell({ config, apps }: TakiShellProps): JSX.Element {
  const [askAIOpen, setAskAIOpen] = useState(false);
  const router = useMemo(() => {
    // TOUT CE QUE LE BUNDLE SAIT SERVIR : la liste de l'org filtre a l'affichage
    // (`installation.ts`), sans reconstruire le routeur a chaque installation.
    const entries = buildAppEntries(Object.keys(apps ?? {}), apps);
    return buildRouter(config, entries);
  }, [config, apps]);

  // CE QUE LES APPS OUVRENT A LIA ET A FLUX, du registre ENTIER du bundle (pas de la
  // liste installee) : c'est l'union que le serveur genere de leurs fiches, et le
  // Studio s'en sert pour nommer la porte qui reste hors de leur portee.
  const ouvertesALia = useMemo(
    () => (apps === undefined ? null : famillesOuvertesDesFiches(Object.values(apps).map((declaration) => declaration.fiche))),
    [apps],
  );

  return <AskTakiState.Provider value={{ open: askAIOpen, setOpen: setAskAIOpen }}>
    <OuverturesIaContext.Provider value={ouvertesALia}>
      <RouterProvider router={router} />
    </OuverturesIaContext.Provider>
  </AskTakiState.Provider>;
}
